import { z } from "zod";
import { SERVICE_SCOPES, TSR_DEVICE_AUTH_METHODS } from "./devices";

/**
 * ============================================================
 * VENUE SYNC — a venue laptop's own copy of its sport, and the way back
 * ============================================================
 * A venue laptop (apps/tsr-desktop's venue host) runs its own API
 * (`API_PROFILE=venue`) on its own database. It is enrolled for ONE sport as
 * a `VENUE_SERVER` device (0058) and holds only that device's revocable
 * service tokens, never a database credential or signing key.
 *
 * DOWN  `GET /api/tsr/venue/snapshot` (VENUE_SERVER service token, read):
 *       every row the venue needs for its sport, sanitised, self-describing
 *       (table, key, rows, in dependency order), so the laptop imports it with
 *       no table-specific code. It is the cloud's view; the laptop replaces its
 *       copy with it, except rows of meets it still has queued saves for.
 *
 * UP    Save Meet on the laptop is applied locally at once AND queued with the
 *       operator's own access token as it was at that moment. When the cloud
 *       is reachable the laptop sends the queue, in order, to
 *       `POST /api/tsr/venue/replay` (VENUE_SERVER service token, write). The
 *       cloud applies each write AS THAT OPERATOR only if: it signed the token
 *       (expiry ignored, age bounded), the token's session wasn't revoked
 *       before the save was made, the operator may still write that meet, and
 *       the meet is the device's sport. Each write applies once (its `id`), and
 *       a write whose slots changed meanwhile comes back `conflict`, never
 *       overwriting.
 * ============================================================
 */

export const TSR_VENUE_SNAPSHOT_VERSION = 1;

/** One table's rows. `key` = its primary-key columns; `rows` = objects keyed by column name (snake_case). */
export interface VenueTableRows {
	table: string;
	key: string[];
	rows: Record<string, unknown>[];
	/**
	 * The column that ties a row to a meet, when there is one (`meet_id`). The
	 * laptop keeps its own rows of meets it has queued saves for.
	 */
	meet_column?: string;
}

export interface VenueSnapshot {
	version: typeof TSR_VENUE_SNAPSHOT_VERSION;
	generated_at: string;
	sport_id: string;
	/**
	 * The cloud's schema: how many migrations it has applied. The laptop
	 * refuses a snapshot from a different schema ("update WSL TSR") rather
	 * than half-importing it.
	 */
	migrations: number;
	/** In dependency order: a table's references come before it. */
	tables: VenueTableRows[];
}

/** The writes a venue laptop queues for the cloud: Save Meet. Paths are under `/api`. */
export const TSR_VENUE_QUEUED_WRITES: readonly { method: "PATCH" | "PUT"; path: RegExp }[] = [
	{ method: "PATCH", path: /^\/api\/meets\/[0-9a-f-]{36}\/results$/i },
	{ method: "PUT", path: /^\/api\/meets\/[0-9a-f-]{36}\/competitors$/i },
];

export const isVenueQueuedWrite = (method: string, path: string): boolean =>
	TSR_VENUE_QUEUED_WRITES.some((w) => w.method === method.toUpperCase() && w.path.test(path));

/** One queued write, as the laptop sends it. */
export interface VenueReplayWrite {
	/** Made on the laptop when the write was queued; the cloud applies each id once. */
	id: string;
	/** When the operator pressed Save (laptop clock, ISO). */
	captured_at: string;
	/** The operator's access token from that request (Bearer value). */
	operator_token: string;
	method: "PATCH" | "PUT";
	/** Under `/api`, e.g. `/api/meets/<id>/results`. */
	path: string;
	body: unknown;
}

export interface VenueReplayRequest {
	/**
	 * In the order they were made. Each is decided on its own: applied,
	 * conflict or rejected. Only a write the cloud cannot decide right now (a
	 * 5xx, a dependency down) stops the batch: it and every later one come back
	 * `deferred`, so the order is kept when the laptop retries.
	 */
	writes: VenueReplayWrite[];
}

export type VenueReplayOutcome =
	/** Applied now, or already applied earlier (same id). */
	| { id: string; status: "applied"; http_status: number }
	/** The meet changed in the cloud meanwhile (a version conflict): the operator must look. */
	| { id: string; status: "conflict"; http_status: number; error: { code: string; message: string } }
	/** Refused for good (revoked session, no longer allowed, wrong sport, too old, bad token). */
	| { id: string; status: "rejected"; http_status: number; error: { code: string; message: string } }
	/** Not tried: an earlier write in this batch could not be decided (the laptop retries later). */
	| { id: string; status: "deferred" };

export interface VenueReplayResponse {
	outcomes: VenueReplayOutcome[];
}

/** How old a queued write may be before the cloud refuses it (an operator's save from last week is not replayed). */
export const TSR_VENUE_REPLAY_MAX_AGE_HOURS = 72;

/** What the laptop's own API reports to the desktop app (`GET /api/venue/status`, 127.0.0.1 only). */
export interface VenueSyncStatus {
	enrolled: boolean;
	sport_id: string | null;
	/** Last snapshot imported (ISO), or null. */
	last_sync_at: string | null;
	last_sync_error: string | null;
	/** Queued Save Meet writes not yet applied in the cloud. */
	pending: number;
	/** Writes the cloud refused or reported as conflicts; the operator must look. */
	attention: { id: string; status: "conflict" | "rejected"; path: string; captured_at: string; message: string }[];
	cloud_reachable: boolean;
}

/**
 * `POST /api/venue/credentials` (the laptop's own API, 127.0.0.1 only): the
 * desktop app hands over what pairing returned (`TsrPairingPoll` approved:
 * `device` + `tokens`), once. `cloud_origin` wins over env
 * `VENUE_CLOUD_ORIGIN`. A re-enrol of the same `tsr_device_id` replaces the
 * tokens; another device while enrolled → 409 `CONFLICT`
 * (`details.reason = "other_device"`). Answers the `VenueSyncStatus`.
 */
export const venueCredentialsBody = z.object({
	cloud_origin: z
		.string()
		.trim()
		.url()
		.regex(/^https?:\/\//i, "http(s) only")
		.optional(),
	device: z
		.object({
			kind: z.literal("SERVICE").optional(),
			tsr_device_id: z.string().uuid(),
			device_kind: z.literal("VENUE_SERVER"),
			auth_method: z.enum(TSR_DEVICE_AUTH_METHODS).optional(),
			sport_id: z.string().uuid(),
			meet_id: z.string().uuid().nullable().optional(),
			station_id: z.string().nullable().optional(),
			scope: z.enum(SERVICE_SCOPES).optional(),
			issued_by_user_id: z.string().uuid().nullable().optional(),
			name: z.string().trim().max(200).optional(),
		})
		.passthrough(),
	tokens: z
		.object({
			access_token: z.string().min(20).max(4000),
			token_type: z.literal("Bearer").optional(),
			expires_in: z.number().int().positive(),
			refresh_token: z.string().min(20).max(500),
			refresh_expires_at: z.string().datetime({ offset: true }).optional(),
			scope: z.enum(SERVICE_SCOPES).optional(),
		})
		.passthrough(),
});
export type VenueCredentialsBody = z.infer<typeof venueCredentialsBody>;
